import { useState, useEffect, useMemo } from "react";
import { 
  Table, 
  Typography, 
  Tag, 
  Alert, 
  Spin, 
  message, 
  Card, 
  Statistic,
  Row,
  Col,
  Input,
  Button,
  Space,
  Tabs,
  Select,
  DatePicker,
  Progress,
  Tooltip,
  Modal,
  Empty
} from "antd";
import { 
  DownloadOutlined, 
  ReloadOutlined, 
  WarningOutlined, 
  StockOutlined,
  SearchOutlined,
  DollarOutlined,
  LineChartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  FileExcelOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Enhanced Stock Status Tag
const StockStatusTag = ({ status, count }) => {
  const statusConfig = {
    'out-of-stock': { color: "#f5222d", text: "Out of Stock", icon: "❌" },
    'low-stock': { color: "#fa8c16", text: "Low Stock", icon: "⚠️" },
    'in-stock': { color: "#52c41a", text: "In Stock", icon: "✓" }
  };

  const config = statusConfig[status] || { color: "gray", text: "Unknown", icon: "?" };

  return (
    <Tag 
      color={config.color}
      style={{ 
        fontWeight: 600, 
        borderRadius: 6,
        padding: "4px 12px",
        fontSize: "13px",
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}
    >
      <span>{config.icon}</span>
      <span>{config.text}</span>
      {count !== undefined && (
        <span style={{ marginLeft: 4, fontWeight: 700 }}>({count})</span>
      )}
    </Tag>
  );
};

// Stock Alert Component
const StockAlert = ({ item }) => {
  const getAlertMessage = (available, status) => {
    switch(status) {
      case 'out-of-stock':
        return `Product ${item.type} is completely out of stock. Urgent restock needed!`;
      case 'low-stock':
        return `Product ${item.type} has only ${available} units left. Consider restocking soon.`;
      default:
        return `Product ${item.type} has sufficient stock (${available} units).`;
    }
  };

  return (
    <Alert
      message={getAlertMessage(item.available, item.status)}
      type={item.status === 'out-of-stock' ? 'error' : item.status === 'low-stock' ? 'warning' : 'info'}
      showIcon
      style={{ marginBottom: 8 }}
    />
  );
};

// Stock Value Chart Component (Simplified)
const StockValueChart = ({ data }) => {
  const sortedData = [...data].sort((a, b) => (b.price * b.available) - (a.price * a.available)).slice(0, 10);
  
  return (
    <div style={{ padding: 16 }}>
      {sortedData.map((item, index) => (
        <div key={item.key} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ width: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.type} ({item.size})
            </Text>
            <Text strong>ETB{(item.price * item.available).toLocaleString()}</Text>
          </div>
          <Progress
            percent={(item.price * item.available / Math.max(...sortedData.map(d => d.price * d.available)) * 100)}
            size="small"
            strokeColor={index % 3 === 0 ? "#1890ff" : index % 3 === 1 ? "#52c41a" : "#722ed1"}
            showInfo={false}
          />
        </div>
      ))}
    </div>
  );
};

const ProductStocks = () => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reportType, setReportType] = useState("stock_status");

  const calculateStockStatus = (available) => {
    if (available <= 0) return 'out-of-stock';
    if (available < 10) return 'low-stock';
    return 'in-stock';
  };

  const fetchStockData = async () => {
    setLoading(true);
    setError("");

    try {
      const [productsRes, salesRes] = await Promise.all([
        axios.get("https://agpipefitting.org.et/api/products"),
        axios.get("https://agpipefitting.org.et/api/sales"),
      ]);

      const products = productsRes.data || [];
      const sales = salesRes.data || [];

      const salesMap = sales.reduce((acc, sale) => {
        const productId = sale.product_id || sale.type;
        acc[productId] = (acc[productId] || 0) + Number(sale.quantity);
        return acc;
      }, {});

      const processedData = products.map(product => {
        const totalStock = Number(product.quantity) || 0;
        const totalSold = salesMap[product.id] || 0;
        const availableStock = totalStock - totalSold;

        return {
          key: product.id,
          id: product.id,
          type: product.type,
          size: product.size,
          stock: totalStock,
          sold: totalSold,
          available: availableStock,
          status: calculateStockStatus(availableStock),
          price: Number(product.price) || 0,
          category: product.category || "Uncategorized",
          unit: product.unit || "pcs",
          lastRestock: product.lastRestock || null
        };
      });

      setStockData(processedData);
      setLastUpdated(new Date());
      message.success("Stock data updated successfully");
    } catch (error) {
      handleFetchError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (error) => {
    const errorMessage = error.response?.data?.message || 
      error.message || 
      "Failed to fetch stock data";
    setError(`Error: ${errorMessage}`);
    message.error("Failed to load stock data");
  };

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = stockData.filter(item =>
      item.type.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (item.size && item.size.toString().toLowerCase().includes(debouncedSearch.toLowerCase()))
    );

    if (statusFilter !== "all") {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    return filtered;
  }, [stockData, debouncedSearch, statusFilter]);

  // Calculate stock metrics
  const stockMetrics = useMemo(() => ({
    totalTypes: stockData.length,
    outOfStock: stockData.filter(i => i.status === 'out-of-stock').length,
    lowStock: stockData.filter(i => i.status === 'low-stock').length,
    inStock: stockData.filter(i => i.status === 'in-stock').length,
    totalAvailable: stockData.reduce((sum, item) => sum + item.available, 0),
    totalSold: stockData.reduce((sum, item) => sum + item.sold, 0),
    totalValue: stockData.reduce((sum, item) => sum + (item.price * item.available), 0),
    totalInvestment: stockData.reduce((sum, item) => sum + (item.price * item.stock), 0),
    turnoverRate: stockData.length > 0 ? 
      (stockData.reduce((sum, item) => sum + item.sold, 0) / 
       stockData.reduce((sum, item) => sum + item.stock, 0) * 100).toFixed(1) : 0
  }), [stockData]);

  // Alert items (low and out of stock)
  const alertItems = useMemo(() => 
    stockData.filter(item => ['low-stock', 'out-of-stock'].includes(item.status)),
    [stockData]
  );

  // Top selling products
  const topSellingProducts = useMemo(() => 
    [...stockData]
      .sort((a, b) => b.sold - a.sold)
      .slice(0, 10)
      .map(item => ({
        ...item,
        salesValue: item.sold * item.price
      })),
    [stockData]
  );

  // Low value items (low stock value)
  const lowValueItems = useMemo(() => 
    [...stockData]
      .filter(item => (item.price * item.available) < 1000)
      .sort((a, b) => (a.price * a.available) - (b.price * b.available)),
    [stockData]
  );

  // High value items
  const highValueItems = useMemo(() => 
    [...stockData]
      .filter(item => (item.price * item.available) > 5000)
      .sort((a, b) => (b.price * b.available) - (a.price * a.available)),
    [stockData]
  );

  // Stock aging report (mock data - in real app would have dates)
  const agingReport = useMemo(() => {
    const now = dayjs();
    return stockData.map(item => {
      const daysInStock = Math.floor(Math.random() * 365); // Mock data
      let agingCategory = "Current";
      if (daysInStock > 180) agingCategory = "Slow Moving";
      if (daysInStock > 365) agingCategory = "Non Moving";
      
      return {
        ...item,
        daysInStock,
        agingCategory
      };
    }).sort((a, b) => b.daysInStock - a.daysInStock);
  }, [stockData]);

  // Columns for different reports
  const overviewColumns = [
    {
      title: "Product",
      dataIndex: "type",
      key: "type",
      render: (_, record) => (
        <div>
          <Text strong>{record.type}</Text>
          {record.size && <Text type="secondary" style={{ marginLeft: 8 }}>({record.size})</Text>}
        </div>
      ),
      sorter: (a, b) => a.type.localeCompare(b.type)
    },
    { 
      title: "Total Stock", 
      dataIndex: "stock", 
      key: "stock",
      sorter: (a, b) => a.stock - b.stock,
      render: (value) => <Text strong>{value}</Text>
    },
    { 
      title: "Sold", 
      dataIndex: "sold", 
      key: "sold",
      sorter: (a, b) => a.sold - b.sold
    },
    {
      title: "Available",
      dataIndex: "available",
      key: "available",
      render: (value, record) => (
        <Space>
          <Text>{value}</Text>
          <StockStatusTag status={record.status} />
        </Space>
      ),
      sorter: (a, b) => a.available - b.available
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (value) => `ETB${value.toLocaleString()}`,
      sorter: (a, b) => a.price - b.price
    },
    {
      title: "Stock Value",
      key: "value",
      render: (_, record) => (
        <Text strong style={{ color: "#1890ff" }}>
          ETB{(record.price * record.available).toLocaleString(undefined, { 
            minimumFractionDigits: 2 
          })}
        </Text>
      ),
      sorter: (a, b) => (a.price * a.available) - (b.price * b.available)
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />} 
          onClick={() => {
            setSelectedProduct(record);
            setModalVisible(true);
          }}
        >
          Details
        </Button>
      )
    }
  ];

  const alertColumns = [
    ...overviewColumns.slice(0, 4),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <StockStatusTag status={status} />
    },
    {
      title: "Action Required",
      key: "action",
      render: (_, record) => {
        const message = record.status === 'out-of-stock' 
          ? "URGENT: Restock immediately" 
          : "Monitor: Low inventory";
        return (
          <Alert
            message={message}
            type={record.status === 'out-of-stock' ? 'error' : 'warning'}
            showIcon
            style={{ padding: '4px 8px' }}
          />
        );
      }
    }
  ];

  const topSellingColumns = [
    ...overviewColumns.slice(0, 3),
    {
      title: "Sales Value",
      key: "salesValue",
      render: (_, record) => (
        <Text strong style={{ color: "#52c41a" }}>
          ETB{(record.price * record.sold).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => (a.price * a.sold) - (b.price * b.sold)
    },
    {
      title: "Sold %",
      key: "soldPercentage",
      render: (_, record) => (
        <Progress 
          percent={((record.sold / record.stock) * 100).toFixed(1)} 
          size="small" 
          strokeColor="#52c41a"
        />
      )
    }
  ];

  const agingColumns = [
    ...overviewColumns.slice(0, 1),
    {
      title: "Days in Stock",
      dataIndex: "daysInStock",
      key: "daysInStock",
      sorter: (a, b) => a.daysInStock - b.daysInStock
    },
    {
      title: "Aging Category",
      dataIndex: "agingCategory",
      key: "agingCategory",
      render: (category) => {
        let color = "green";
        if (category === "Slow Moving") color = "orange";
        if (category === "Non Moving") color = "red";
        return <Tag color={color}>{category}</Tag>;
      }
    },
    ...overviewColumns.slice(1, 6)
  ];

  // CSV data for export
  const getCSVData = () => {
    switch(reportType) {
      case 'stock_status':
        return filteredData;
      case 'alerts':
        return alertItems;
      case 'top_selling':
        return topSellingProducts;
      case 'aging':
        return agingReport;
      default:
        return filteredData;
    }
  };

  useEffect(() => {
    fetchStockData();
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 1800, margin: "0 auto" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <StockOutlined /> Inventory Management System
          </Title>
          {lastUpdated && (
            <Text type="secondary">
              Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString()}
            </Text>
          )}
        </Col>
        
        <Col>
          <Space>
            <Select
              value={reportType}
              onChange={setReportType}
              style={{ width: 180 }}
            >
              <Option value="stock_status">Stock Status Report</Option>
              <Option value="alerts">Alerts Report</Option>
              <Option value="top_selling">Top Selling Report</Option>
              <Option value="aging">Aging Report</Option>
            </Select>
            
            <CSVLink 
              data={getCSVData()}
              filename={`stock-report-${reportType}-${new Date().toISOString().slice(0, 10)}.csv`}
            >
              <Button type="primary" icon={<FileExcelOutlined />}>
                Export {reportType.replace('_', ' ')}
              </Button>
            </CSVLink>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={fetchStockData}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Quick Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Total Products"
              value={stockMetrics.totalTypes}
              prefix={<StockOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Total Stock Value"
              value={stockMetrics.totalValue}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#52c41a" }}
              formatter={value => `ETB${Number(value).toLocaleString(undefined, { 
                minimumFractionDigits: 2 
              })}`}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Out of Stock"
              value={stockMetrics.outOfStock}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Low Stock"
              value={stockMetrics.lowStock}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Total Sold"
              value={stockMetrics.totalSold}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card bordered={false} hoverable>
            <Statistic
              title="Turnover Rate"
              value={stockMetrics.turnoverRate}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ color: "#13c2c2" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Status Summary */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Stock Status Summary" bordered={false}>
            <Row gutter={16}>
              <Col span={8}>
                <Space>
                  <StockStatusTag status="in-stock" count={stockMetrics.inStock} />
                  <Text>Healthy stock levels</Text>
                </Space>
              </Col>
              <Col span={8}>
                <Space>
                  <StockStatusTag status="low-stock" count={stockMetrics.lowStock} />
                  <Text>Need monitoring</Text>
                </Space>
              </Col>
              <Col span={8}>
                <Space>
                  <StockStatusTag status="out-of-stock" count={stockMetrics.outOfStock} />
                  <Text>Require immediate action</Text>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 24 }} bordered={false}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Input
              placeholder="Search products by type or size..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              size="large"
            >
              <Option value="all">All Status</Option>
              <Option value="in-stock">In Stock</Option>
              <Option value="low-stock">Low Stock</Option>
              <Option value="out-of-stock">Out of Stock</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="View Mode"
              style={{ width: '100%' }}
              value={viewMode}
              onChange={setViewMode}
              size="large"
            >
              <Option value="table">Table View</Option>
              <Option value="cards">Card View</Option>
              <Option value="charts">Chart View</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button 
              icon={<FilterOutlined />}
              onClick={() => {
                setStatusFilter("all");
                setSearchTerm("");
              }}
              style={{ width: '100%' }}
              size="large"
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Main Content Tabs */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        type="card"
        size="large"
        style={{ marginBottom: 24 }}
      >
        <TabPane tab="Overview" key="overview" icon={<StockOutlined />}>
          <Table
            dataSource={filteredData}
            columns={overviewColumns}
            loading={loading}
            pagination={{ 
              pageSize: 10, 
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '25', '50', '100']
            }}
            scroll={{ x: true }}
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Totals:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong>{stockMetrics.totalAvailable}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Text strong>ETB{stockMetrics.totalValue.toLocaleString()}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </TabPane>

        <TabPane tab="Alerts" key="alerts" icon={<WarningOutlined />}>
          {alertItems.length === 0 ? (
            <Empty
              description="No stock alerts at this time"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div>
              <Alert
                message={`${alertItems.length} products require attention`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Table
                dataSource={alertItems}
                columns={alertColumns}
                pagination={false}
                bordered
              />
            </div>
          )}
        </TabPane>

        <TabPane tab="Top Selling" key="topSelling" icon={<BarChartOutlined />}>
          <Row gutter={16}>
            <Col span={16}>
              <Table
                dataSource={topSellingProducts}
                columns={topSellingColumns}
                pagination={false}
                bordered
                title={() => "Top 10 Selling Products"}
              />
            </Col>
            <Col span={8}>
              <Card title="Sales Distribution" bordered={false}>
                <StockValueChart data={topSellingProducts} />
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane tab="Aging Report" key="aging" icon={<LineChartOutlined />}>
          <Alert
            message="Stock aging helps identify slow-moving and non-moving inventory"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            dataSource={agingReport}
            columns={agingColumns}
            pagination={{ pageSize: 10 }}
            bordered
          />
        </TabPane>

        <TabPane tab="Value Analysis" key="value" icon={<DollarOutlined />}>
          <Row gutter={16}>
            <Col span={12}>
              <Card title="High Value Items (Value > ETB 5,000)" bordered={false}>
                <Table
                  dataSource={highValueItems}
                  columns={overviewColumns.filter(col => 
                    ['type', 'available', 'price', 'value'].includes(col.key || col.dataIndex)
                  )}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Low Value Items (Value < ETB 1,000)" bordered={false}>
                <Table
                  dataSource={lowValueItems}
                  columns={overviewColumns.filter(col => 
                    ['type', 'available', 'price', 'value'].includes(col.key || col.dataIndex)
                  )}
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error Loading Data"
          description={error}
          type="error"
          showIcon
          closable
          action={
            <Button 
              size="small" 
              danger 
              onClick={fetchStockData}
            >
              Retry
            </Button>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Product Detail Modal */}
      <Modal
        title="Product Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedProduct && (
          <Row gutter={24}>
            <Col span={12}>
              <Card title="Basic Information" size="small">
                <p><strong>Type:</strong> {selectedProduct.type}</p>
                <p><strong>Size:</strong> {selectedProduct.size || "N/A"}</p>
                <p><strong>Category:</strong> {selectedProduct.category}</p>
                <p><strong>Unit:</strong> {selectedProduct.unit}</p>
                <p><strong>Price:</strong> ETB{selectedProduct.price.toLocaleString()}</p>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Stock Information" size="small">
                <p><strong>Total Stock:</strong> {selectedProduct.stock}</p>
                <p><strong>Sold:</strong> {selectedProduct.sold}</p>
                <p><strong>Available:</strong> {selectedProduct.available}</p>
                <p><strong>Status:</strong> <StockStatusTag status={selectedProduct.status} /></p>
                <p><strong>Stock Value:</strong> ETB{(selectedProduct.price * selectedProduct.available).toLocaleString()}</p>
              </Card>
            </Col>
            <Col span={24} style={{ marginTop: 16 }}>
              <StockAlert item={selectedProduct} />
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default ProductStocks;